import {SpaceTr, Tr, TrSpace} from "@/i18n/i18n";
import {injectable} from "inversify";
import {Keys} from "@/constants/key";
import {
    BankGift,
    BankOption,
    BonusStatusInfo,
    BoosterDto,
    BoostersDto,
    RewardedAdvType,
    ShopArtifact,
    ShopArtifactContext,
    ShopCreature,
    SimpleShopArtifact,
    SumType
} from "@/store/shop/types";
import {ShopCreaturePanel} from "@/scenes.ui/shop-creature-panel";
import {ShopBankPanel} from "@/scenes.ui/shop-bank-panel";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {BlackoutUtils} from "@/utils/blackout-utils";
import {CreatureUtils} from "@/utils/creature-utils";
import {ArtifactShopPanel} from "@/scenes.ui/artifact-shop-panel";
import {ArtifactBuyPanel} from "@/scenes.ui/artifact-buy-panel";
import {CampaignType, OfferType, Player, UserSource} from "@/store/player/types";
import {IntegrationUtils} from "@/utils/integration-utils";
import {PaymentNotificationService} from "@/services/payment-notification-service";
import {NewCreaturePanel} from "@/scenes.ui/new-creature-panel";
import {BuyCreatureConfirmationPanel} from "@/scenes.ui/buy-creature-confirmation-panel";
import SoundUtils from "@/utils/sound-utils";
import PlayerStore from "@/store/player";
import ShopStore from "@/store/shop";
import {TabbedPanel} from "@/scenes.ui/tabbed-panel";
import PackUtils from "@/utils/pack-utils";
import {PackBankPanel} from "@/scenes.ui/pack-bank-panel";
import {Settings} from "@/constants/settings";
import {ListTextPanel} from "@/scenes.ui/list-text-panel";
import {ShopBoosterPanel} from "@/scenes.ui/shop-booster-panel";
import {ConfirmationPanel} from "@/scenes.ui/confirmation-panel";
import {BeelinePayConfirmationPanel} from "@/scenes.ui/beeline-pay-confirmation-panel";
import BoosterUtils from "@/utils/booster-utils";
import {BonusStatusPanel} from "@/scenes.ui/bonus-status-panel";
import {ListService} from "@/services/list-service";
import {RewardedAdvPanel} from "@/scenes.ui/rewarded-adv-panel";
import {InfoPanel} from "@/scenes.ui/info-panel";
import {CASService} from '@/cas'
import http from "@/axios";
import RewardedUtils from "@/utils/rewarded-utils";
import {PaymentChangeService} from "@/services/payment-change-service";
import {PaymentChangePanelBank} from "@/scenes.ui/payment-change-panel-bank";
import {IapService} from "@/services/iap-service";
import {IAPProduct} from "@awesome-cordova-plugins/in-app-purchase-2/ngx";
import {EarnGemsPanel} from "@/scenes.ui/earn-gems-panel";
import {EarnGemsDetailsPanel} from "@/scenes.ui/earn-gems-details-panel";
import {GiftBankPanel} from "@/scenes.ui/gift-bank-panel";
import {SuperPackPanel} from "@/scenes.ui/super-pack-panel";
import ListenerManager from "@/managers/listener-manager";
import {ChestDescPanel} from "@/scenes.ui/chest-desc-panel";
import {NotifManager} from "@/managers/notif-manager";
import Scene = Phaser.Scene;
import BaseSound = Phaser.Sound.BaseSound;
import Container = Phaser.GameObjects.Container;
import ArtifactUtils from "@/utils/artifact-utils";
import {ListBlackouter} from "@/utils/list-blackouter";
import ArtifactStore from "@/store/artifact";
import {GroupInfo} from "@/store/artifact/types";
import {TextPanel} from "@/scenes.ui/text-panel";
import {ArtLevelLine} from "@/scenes.ui/art-level-line";

@injectable()
export class ShopService {

    protected static readonly MAX_CREATURES = 6;
    protected scene: Scene | undefined;
    protected canvas: HTMLCanvasElement | undefined;

    protected closeCallback!: () => void;

    protected awardSound!: BaseSound | undefined;
    protected selectSound!: BaseSound | undefined;
    protected levelUpSound!: BaseSound | undefined;
    protected clickSound!: BaseSound | undefined;

    private noValuteFeedback!: boolean;

    protected paymentNotificationService!: PaymentNotificationService;

    protected tabbedPanel!: TabbedPanel;

    protected statClickCallback!: (creatureId: string) => unknown;

    protected listService!: ListService;

    protected paymentChangeService!: PaymentChangeService;

    protected iapService!: IapService;

    public static logClient(message: string): Promise<void> {
        const params = new URLSearchParams();
        params.append('message', message);
        params.append('domain', `Mobile app ${process.env.VUE_APP_MOBILE_APP_TYPE} v${process.env.VUE_APP_ANDROID_VERSION_CODE}`);
        return http.post('/api/log/add', params);
    }

    public openShop(scene: Scene, canvas: HTMLCanvasElement, noValuteFeedback: boolean, closeCallback: () => void,
                    paymentNotificationService: PaymentNotificationService, statClickCallback: (creatureId: string) => unknown,
                    tabName: TabName, listService: ListService, paymentChangeService: PaymentChangeService,
                    iapService: IapService) {
        this.scene = scene;
        this.canvas = canvas;
        this.closeCallback = closeCallback;
        this.noValuteFeedback = noValuteFeedback;
        this.paymentNotificationService = paymentNotificationService;
        this.statClickCallback = statClickCallback;
        this.listService = listService;
        this.paymentChangeService = paymentChangeService;
        this.iapService = iapService;

        IntegrationUtils.initPaymentAPI(PlayerStore.getPlayer());

        const self = this;

        BlackoutUtils.blackout(scene, canvas, () => {
            self.awardSound = SoundUtils.addSound(self.scene!, Keys.SOUND_AWARD);
            self.selectSound = SoundUtils.addSound(self.scene!, Keys.SOUND_SELECT);
            self.levelUpSound = SoundUtils.addSound(self.scene!, Keys.SOUND_LEVEL_UP);
            self.clickSound = SoundUtils.addSound(self.scene!, Keys.SOUND_COMPLETED);

            self.tabbedPanel = self.createTabbedPanel();
            self.tabbedPanel.setAgitationText(Tr('ne.hvataet.izumrudov.kupite.v.banke'));
            scene.add.existing(self.tabbedPanel);

            if (noValuteFeedback) {
                new FeedbackPanel(scene, Tr('ne.hvataet.izumrudov'), self.tabbedPanel, false).show();
            }

            switch (tabName) {
                case TabName.BANK:
                    self.tabbedPanel.goToTab('bank');
                    break;
                case TabName.ARTIFACT:
                    self.tabbedPanel.goToTab('artifacts');
                    break;
                case TabName.HERO:
                    self.tabbedPanel.goToTab('heroes');
                    break;
                case TabName.BOOSTER:
                    self.tabbedPanel.goToTab('boosters');
                    break;
                case TabName.CHAIN:
                    self.tabbedPanel.goToTab('chain');
                    break;
                case TabName.SALE:
                    self.tabbedPanel.goToTab('sale');
                    break;
                case TabName.GIFT:
                    self.tabbedPanel.goToTab('gift');
                    break;
            }

            return this;
        });

        ListenerManager.setPurchaseRecievedCallback(() => {
            self.tabbedPanel.closePanel();
        });
    }

    public closeShop(): void {
        if (this.closeCallback) {
            this.closeCallback();
        }
    }

    protected populateBoosterTab(re: boolean): void {
        let self = this;

        ShopStore.fetchBoosters().then((boostersDto: BoostersDto) => {
            self.doPopulateBoosterTab(boostersDto, re);
        });
    }

    private doPopulateBoosterTab(boostersDto: BoostersDto, re: boolean): void {
        let self = this;
        this.tabbedPanel.setAgitationText(Tr('now') + boostersDto.currentTime);

        let boosters: BoosterDto[] = boostersDto.boosters;
        let panels: Container[] = [];

        let panelY = 0;
        boosters.forEach(b => {
            let bt = b.boosterType;
            let scp = new ShopBoosterPanel(this.scene!, 0, panelY, b, () => {
                if (PlayerStore.getPlayer().mithril < b.finalPrice) {
                    SoundUtils.play(self.levelUpSound);
                    this.tabbedPanel.goToTab('bank');
                    new FeedbackPanel(self.scene!, Tr('ne.hvataet.izumrudov'), self.tabbedPanel!, false).show();
                } else if (b.finalPrice > 0 && b.sumType !== SumType.UNAVAILABLE) {

                    let confirmPanel: ConfirmationPanel = new ConfirmationPanel(this.scene!, this.canvas!, () => {
                        SoundUtils.play(self.awardSound);
                        ShopStore.buyBooster(b.boosterType).then((boostersDto: BoostersDto) => {
                            self.doPopulateBoosterTab(boostersDto, true);
                        });
                    }, Tr('podtverzhdenie'), ShopService.getConfText(b), BoosterUtils.getIcon(bt),
                        Tr('da'), Tr('net'), Keys.ATLAS_BOOST);
                    confirmPanel.open();
                } else {
                    new FeedbackPanel(this.scene!, Tr('no.boo'), undefined, false).show();
                }
            });

            panels.push(scp);
            panelY += ShopCreaturePanel.HEIGHT;

        });

        if (re) {
            this.tabbedPanel.resupplyChildren('boosters', panels);
        } else {
            this.tabbedPanel.supplyChildren('boosters', panels);
        }
    }

    private static getConfText(b: BoosterDto): string {
        switch (b.sumType) {
            case SumType.REPLACE:
                return Tr('repl.boo');
            case SumType.BUY:
                return Tr('boo.act') + b.name + '?';
            case SumType.APPEND:
                return Tr('prol.boo') + b.hours + ' ' + Tr('hrs') + '?';
        }
        return Tr('no.boo');
    }

    protected populateHeroesTab(): void {
        let self = this;

        let player = PlayerStore.getPlayer()!;
        this.setupHeroAgitations(player);

        this.getFetchHeroesAction()().then(() => {
            const shopCreatures: ShopCreature[] = ShopStore.getShopCreatures();
            let panels: ShopCreaturePanel[] = [];
            let panelY = 0;
            shopCreatures.forEach(sc => {
                let classText = CreatureUtils.getClassName(sc.creature.warriorClass);
                if (!classText) {
                    return;
                }
                let scp = new ShopCreaturePanel(this.scene!, 0, panelY, sc, self.statClickCallback, () => {
                    if (PlayerStore.getPlayer().mithril < sc.price) {
                        SoundUtils.play(self.levelUpSound);
                        this.tabbedPanel.goToTab('bank');
                        new FeedbackPanel(self.scene!, Tr('ne.hvataet.izumrudov'), self.tabbedPanel!, false).show();
                    } else {
                        let isSquadFull = CreatureUtils.findNonTemporalCreatures(PlayerStore.getPlayer().creatures).length >= ShopService.MAX_CREATURES;
                        let confirmPanel: BuyCreatureConfirmationPanel = new BuyCreatureConfirmationPanel(this.scene!, this.canvas!,
                            this.tabbedPanel!.depth + 1, sc, () => {
                                SoundUtils.play(self.awardSound);
                                ShopStore.buyCreature(sc.id).then(() => {
                                    sc.creatures.forEach(c => {
                                        new NewCreaturePanel(self.scene!, self.canvas!, c).open();
                                    });
                                });
                            }, isSquadFull, ShopService.MAX_CREATURES);
                        confirmPanel.open();
                    }
                });

                panels.push(scp);
                panelY += ShopCreaturePanel.HEIGHT;
            });

            this.tabbedPanel.supplyChildren('heroes', panels);
        });
    }

    private createBankGiftByPositionMap(bankOptions: BankOption[], bankGifts: BankGift[]): Map<number, BankGift> {
        let res = new Map<number, BankGift>();

        for (let bg of bankGifts) {
            let pos = Math.floor(Math.random() * bankOptions.length);

            let counter = 0;
            while (res.has(pos)) {
                pos = Math.floor(Math.random() * bankOptions.length);
                ++counter;
                if (counter > 100) {
                    break;
                }
            }
            res.set(pos, bg);
        }

        return res;
    }

    protected populateBankTab(): void {
        let self = this;
        let player = PlayerStore.getPlayer();
        player.haveBankNews = false;
        ShopStore.fetchBankInfo().then(() => {
            let camps: CampaignType[] = PlayerStore.getCampaignTypes();
            this.tabbedPanel.setAgitationText(Tr('ne.hvataet.izumrudov.kupite.v.banke'));
            if (camps) {
                for (let camp of camps) {
                    switch (camp) {
                        case CampaignType.CHEST:
                        case CampaignType.CHEST_2:
                        case CampaignType.CHEST_3:
                            this.tabbedPanel.setAgitationText(Tr('c.chest'));
                            break;
                        case CampaignType.WHEEL:
                            this.tabbedPanel.setAgitationText(Tr('c.wheel2'));
                            break;
                        case CampaignType.HERO:
                            this.tabbedPanel.setAgitationText(Tr('c.hero2'));
                            break;
                        case CampaignType.GEMS_PLUS_50:
                            this.tabbedPanel.setAgitationText(Tr('c.gems_plus_50_2'));
                            break;
                        case CampaignType.SNOW_WORLD:
                            this.tabbedPanel.setAgitationText(Tr('c.snow_world2'));
                            break;
                        case CampaignType.BOOSTER:
                            this.tabbedPanel.setAgitationText(Tr('c.boosters2'));
                            break;
                        case CampaignType.PORTAL_DISCOUNT:
                            this.tabbedPanel.setAgitationText(Tr('c.portal_discount2'));
                            break;
                    }
                }
            }
            const bankOptions: BankOption[] = ShopStore.getBankOptions();
            const bankGifts: BankGift[] = ShopStore.getBankGifts();
            const panels: Container[] = [];
            let panelY = this.showBankOptions(bankOptions, bankGifts, panels, player);

            let ri = ShopStore.getRewardedInfo()!;
            if (ri.enabled) {
                let rewPanel: RewardedAdvPanel = new RewardedAdvPanel(this.scene!, 0, panelY, ri, () => {
                    if (ri.todayWatchTimes >= ri.maxWatchTimes) {
                        new FeedbackPanel(self.scene!, Tr('rew.lim'), self.tabbedPanel, false).show();
                    } else {
                        RewardedUtils.watchRewarded(self.scene!, RewardedAdvType.GEMS, () => {
                            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('aw'),
                                TrSpace('gt') + ri.reward + SpaceTr('gems.mul.gen').toUpperCase(), TrSpace('rew.res') + String(ri.todayWatchTimes + 1) + '/' + String(ri.maxWatchTimes), () => {
                                    ShopStore.takeGemsForRewardedAdv();
                                }, Tr('prinjat'));
                            ip.openAnimated();
                        });
                    }
                });
                panels.push(rewPanel);
                panelY += ShopBankPanel.HEIGHT;
            }

            if (IntegrationUtils.isReferralSystemEnabled()){//} && PlayerStore.hasModerAccess()){
                panels.push(new EarnGemsPanel(this.scene!, this.canvas!,() => {
                    PlayerStore.getMyRefCode().then(code => {
                        new EarnGemsDetailsPanel(self.scene!, self.canvas!, code).open();
                    })
                }));
            }

            panels.push(new ChestDescPanel(this.scene!, this.canvas!));

            if (player.userSource === UserSource.own && process.env.VUE_APP_IS_MOBILE_APP !== 'true' && !IntegrationUtils.isEnglishServer()) {
                panels.push(new PaymentChangePanelBank(this.scene!, () => {
                    this.paymentChangeService.open(self.scene!, self.canvas!);
                }));
            }

            let bi: BonusStatusInfo | undefined = ShopStore.getBonusStatusInfo();
            if (bi) {
                panels.push(new BonusStatusPanel(this.scene!, bi, () => self.listService.showManual(self.scene!,
                    self.canvas!, 'bonusPoints', () => {})));
            }

            if (player.bestVictoryStair >= Settings.MYTH_HERO_MIN_STAIR) {
                panels.push(new ListTextPanel(self.scene!, Tr('myth.in.clan'), 60));
            }

            this.tabbedPanel.supplyChildren('bank', panels);
        });
    }

    protected showBankOptions(bankOptions: BankOption[], bankGifts: BankGift[], panels: Container[], player: Player, setupPurchaseCallback: boolean = true, setupNotifCheck: boolean = false, showBonuses: boolean = true): number {
        const externalUser: boolean = IntegrationUtils.isExternalUser();
        const giftByPositionMap: Map<number, BankGift> = this.createBankGiftByPositionMap(bankOptions, bankGifts);
        let counter: number = 0;
        let panelY = 0;

        const self = this;

        bankOptions.forEach((bo, i) => {
            var googleProduct: IAPProduct | undefined = undefined
            if (IapService.IapEnabled()) {
                googleProduct = self.iapService.products.find(el => el.id === "gems_" + (bo.gems / 10))
            }
            const callback = () => {
                SoundUtils.play(self.awardSound);
                // @ts-ignore
                if (typeof Window.ensureNoFullscreen === 'function') {
                    // @ts-ignore
                    Window.ensureNoFullscreen();
                }

                if (setupPurchaseCallback) {
                    self.paymentNotificationService.setupPaymentCheckingTask();
                }

                if (setupNotifCheck) {
                    NotifManager.setupNotifCheckingTask(self.scene!, self.canvas!);
                }

                if (IapService.IapEnabled()) {
                    if (googleProduct !== undefined) {
                        console.log(`PurchaseStore: Trying to Google Purchase. JSON Product: ${JSON.stringify(googleProduct)}`)
                        ShopService.logClient(`PurchaseStore: Trying to Google Purchase. JSON Product: ${JSON.stringify(googleProduct)}`)
                        try {
                            self.iapService.purchase(googleProduct)
                            console.log("PurchaseStore: Success")
                            ShopService.logClient("PurchaseStore: Success")
                        } catch (e) {
                            console.log(`PurchaseStore: ERROR Google Purchase ${e.message}`)
                            ShopService.logClient(`PurchaseStore: ERROR Google Purchase ${e.message}`)
                        }
                    } else {
                        console.log(`PurchaseStore: Can not to Google Purchase. Google Product is undefined`)
                        ShopService.logClient(`PurchaseStore: Can not to Google Purchase. Google Product is undefined`)
                    }
                    return
                }

                if (player.userSource === UserSource.ok) {
                    let gemsCount = bo.gems + bo.bonus;
                    IntegrationUtils.showOkPaymentDialog(bo.price, String(gemsCount) + Tr('izumrudov'),
                        Tr('game.credits'), String(gemsCount) + ' gems');

                } else if (player.userSource === UserSource.vk) {
                    IntegrationUtils.showVkPaymentDialog(bo.id);
                } else if (player.userSource === UserSource.ya) {
                    IntegrationUtils.showYaPaymentDialog(bo.id,() => {});
                } else if (player.userSource === UserSource.tg) {
                    IntegrationUtils.showTelegramPaymentDialog(bo.id);
                } else if (IntegrationUtils.isGPUserSource(player.userSource)) {
                    IntegrationUtils.showGamePushPaymentDialog(bo.id);
                } else if (player.userSource === UserSource.exe) {
                    IntegrationUtils.showExePaymentDialog(bo.id);
                } else if (player.userSource === UserSource.beeline) {
                    new BeelinePayConfirmationPanel(self.scene!, self.canvas!, () => {
                        ShopStore.buyValuteBeeline(bo.price);
                    }, Tr('beeline.payment.confirmation.title'), Tr('beeline.payment.confirmation.text'), Keys.UI_GOLDEN_CHECK, Tr('beeline.payment.confirmation.accept_text'), Tr('beeline.payment.confirmation.decline_text'), Keys.ATLAS_PROFILE).open();
                } else if (player.userSource === UserSource.mystore) {
                    IntegrationUtils.showMystorePaymentDialog(bo.id, bo.price, bo.gems + bo.bonus, bo.valuteName);
                } else if (player.userSource === UserSource.foto) {
                    IntegrationUtils.showFotoPaymentDialog(bo);
                } else if (player.userSource === UserSource.kong) {
                    IntegrationUtils.showKongregatePaymentDialog(bo);
                } else if (player.userSource === UserSource.crazygames) {
                    ShopStore.buyValuteXsollaIFrame(bo.price).then((token: string) => {
                        // window.location.href = url;

                        var options = {
                            access_token: token,
                            iframeOnly: true,
                            childWindow: {
                                target: '_self'
                            },
                            lightbox: {
                                modal: false
                            }
                        };

                        // @ts-ignore
                        XPayStationWidget.init(options);
                        // @ts-ignore
                        XPayStationWidget.open();

                    });

                } else if (player.userSource === UserSource.own && IntegrationUtils.isRustoreUser()) {
                    IntegrationUtils.showRustorePaymentDialog(bo.id);
                } else if (externalUser) {
                    ShopStore.buyValuteSocial(bo.price).then((url: string) => {
                        window.location.href = url;
                    });
                } else {
                    let host = window.location.hostname;
                    if (host.includes('lgtest.fun')) {
                        ShopStore.buyValuteStub(bo.price);
                    } else {
                        //@ts-ignore
                        if (window.VK) {
                            //@ts-ignore
                            window.VK.Goal('purchase', {value: bo.price})
                        }
                        if (self.paymentChangeService.isUseXsolla()) {
                            ShopStore.buyValuteXsolla(bo.price).then((url: string) => {
                                window.location.href = url;
                            });
                        } else {
                            ShopStore.buyValuteRobokassa(bo.price, bo.title).then((url: string) => {
                                window.location.href = url;
                            });
                        }
                    }
                }
            };

            let scp;
            let googleProductPriceView: string | undefined = undefined;

            if (googleProduct !== undefined) {
                googleProductPriceView = googleProduct.price
            }
            if (bo.decorationId === 'pack_3') {
                scp = new PackBankPanel(self.scene!, 0, panelY, bo, player, player.userSource, callback);
            } else if (bo.decorationId === 'pack_1' || bo.decorationId === 'pack_2'
                || SuperPackPanel.PACKS_152.includes(bo.decorationId)
                || SuperPackPanel.PACKS_176.includes(bo.decorationId)
                || SuperPackPanel.PACKS_190.includes(bo.decorationId)
                || bo.decorationId === 'pack_8'|| bo.decorationId === 'pack_7'
                || bo.decorationId === 'pack_ny_1' || bo.decorationId === 'pack_ny_2' || bo.decorationId === 'pack_mar_1' || bo.decorationId === 'pack_mar_2') {
                let panel = new SuperPackPanel(self.scene!, 0, panelY, bo, player, player.userSource, () => {
                    if (bo.price > 0) {
                        callback();
                    } else {
                        PackUtils.visualizeTake(self.scene!, panel.getItems(), self.clickSound!);
                        panel.setRecieved(true);
                        ShopStore.takeBankFreeOption(bo.id);
                    }
                }, (creatureId: string) => {
                    self.listService.showManual(self.scene!, self.canvas!, creatureId, () => {});
                }, false, googleProductPriceView);
                scp = panel;
            } else {
                scp = new ShopBankPanel(self.scene!, 0, panelY, bo, player, player.userSource, callback, false, googleProductPriceView, showBonuses);
            }

            panels.push(scp);
            if (process.env.VUE_APP_CAS_ENABLED === 'true'
                && process.env.VUE_APP_IS_MOBILE_APP === 'true'
                && process.env.VUE_APP_CAS_REWARD_ENABLED === 'true'
                && i == 0) {
                let isReward = true;
                let realPrice = bo.price;
                bo.price = 0;
                panels.push(new ShopBankPanel(self.scene!, 0, panelY, bo, player, player.userSource, () => {
                    const params = new URLSearchParams();
                    params.append('price', String(realPrice));
                    http.post('/api/cas/result', params).then(function (response) {
                        console.log(response);
                    }).catch(function (error) {
                        console.log(error);
                    });
                    let cas = new CASService();
                    cas.showAdReward(() => {
                        console.log("CAS Reward video was shown")
                        },
                        () => {
                            console.log("CAS Reward video was complete")
                        });
                }, isReward));
            }

            panelY += ShopBankPanel.HEIGHT;

            console.log('counter = ' + counter);

            if (showBonuses && giftByPositionMap.has(counter)) {
                console.log('Yes!');
                let bankGift: BankGift = giftByPositionMap.get(counter)!;

                let bgp = new GiftBankPanel(self.scene!, 0, panelY, bankGift, () => {
                    ShopStore.takeBankGift(bankGift.id);
                    bgp.hideTakeButton();
                    new FeedbackPanel(self.scene!, Tr('nagrada.poluchena'), undefined, true).show();
                });
                panels.push(bgp);
                panelY += GiftBankPanel.HEIGHT;
            }

            ++counter;
        });

        return panelY;
    }

    protected populateArtifactTab(): void {
        this.doPopulateArtifactTab(ShopArtifactContext.COMMON_SHOP);
    }

    protected doPopulateArtifactTab(context: ShopArtifactContext): void {
        let self = this;

         ShopStore.fetchShopItemOptions(context).then(() => {
            self.tabbedPanel.setAgitationText(Tr('ne.hvataet.izumrudov.kupite.v.banke'));
            const player: Player = PlayerStore.getPlayer();
            const shopArtifactOptions: ShopArtifact[] = ShopStore.getArtifactOptions();
            let panels: ArtifactShopPanel[] = [];

            shopArtifactOptions.forEach(sa => {
                let sap = new ArtifactShopPanel(this.scene!, 0, 0, sa, (artifactId:string) => {
                    ShopService.showArtifactManual(self.scene!, self.canvas!, self.listService, artifactId);
                },() => {
                    let artifactsToBuy = sa.artifacts.slice();
                    // Если цена предмета больше, чем кристаллов у юзера - направляем в банк
                    if (sa.price.mithril > player.mithril) {

                        this.tabbedPanel.goToTab('bank');

                        new FeedbackPanel(self.scene!,
                            Tr('dlja.pokupki.ne.hvataet') + (sa.price.mithril - PlayerStore.getPlayer().mithril) + ' ' + Tr('gems.mul.gen') + '!',
                            self.tabbedPanel!, false).show();

                    } else {
                        this.openArtifactBuyPanel(player, sa, artifactsToBuy, [], 0, context);
                    }
                });

                panels.push(sap);
            });
            self.tabbedPanel.supplyChildren('artifacts', panels);
        });
    }

    private openArtifactBuyPanel(player: Player, sa: ShopArtifact, artifactsToBuy: SimpleShopArtifact[],
                                 chosenCreatures: number[], unlockSumPrice:number, context: ShopArtifactContext){
        let self = this;
        let simpleArtifact = artifactsToBuy[0];
        let totalArtifactsCount = artifactsToBuy.length + chosenCreatures.length;
        artifactsToBuy.splice(0, 1);

        new ArtifactBuyPanel(this.scene!, this.canvas!, simpleArtifact, sa.price, chosenCreatures, totalArtifactsCount, unlockSumPrice,
            (unlockSumPrice: number, chosenCreatures: number[]) => {

            // Если цена артефакт + анлок больше, чем есть кристаллов - переправляем в магазин
                console.log("finalArtifactPrice: " + sa.price.mithril + unlockSumPrice)
            if (player.mithril < sa.price.mithril + unlockSumPrice) {
                this.tabbedPanel.goToTab('bank');
                new FeedbackPanel(self.scene!, Tr('ne.hvataet.izumrudov'), self.tabbedPanel, false).show();
            } else if (artifactsToBuy.length > 0) { //открываем панель для следующего артефакта
                this.openArtifactBuyPanel(player, sa, artifactsToBuy, chosenCreatures, unlockSumPrice, context);
            } else {  // если хватает - покупаем
                ShopStore.buyArtifact(sa.bunchId, chosenCreatures).then(() => {
                    SoundUtils.addAndPlay(self.scene!, Keys.SOUND_AWARD);
                    new FeedbackPanel(self.scene!, totalArtifactsCount > 1? Tr('predmeti.uspeshno.priobreteni') : Tr('predmet.uspeshno.priobreten'),
                        self.tabbedPanel, true).show();
                    this.doPopulateArtifactTab(context);
                })
            }
        }).open();
    }

    protected createTabbedPanel(): TabbedPanel {
        let self = this;
        return new TabbedPanel(this.scene!, this.canvas!, Tr('magazin'), ['bank', 'artifacts', 'heroes', 'boosters'],
            [Tr('bank'), Tr('predmeti'), Tr('geroi'), Tr('boosters')], [true, true, true, true], this.selectSound, (tabId: string) => {
                if (tabId === 'bank') {
                    this.populateBankTab();
                } else if (tabId === 'artifacts') {
                    this.populateArtifactTab();
                } else if (tabId === 'heroes') {
                    this.populateHeroesTab();
                } else if (tabId === 'boosters') {
                    this.populateBoosterTab(false);
                }
            }, () => {}, () => self.closeCallback());
    }

    protected getFetchHeroesAction(): () => Promise<void> {
        return ShopStore.fetchShopCreatures;
    }

    protected setupHeroAgitations(player: Player) {
        if (player.offers) {
            for (let offer of player.offers) {
                if (offer.offerType === OfferType.FIRST_HERO) {
                    this.tabbedPanel.setAgitationText(Tr('skidka.na.pervogo.geroja'));
                } else if (offer.offerType === OfferType.ALL_CREATURES_DISCOUNT) {
                    this.tabbedPanel.setAgitationText(Tr('skidki.na.vseh.geroev'));
                } else if (offer.offerType === OfferType.GREEN_DRAGON_DISCOUNT || offer.offerType === OfferType.BLACK_DRAGON_DISCOUNT) {
                    this.tabbedPanel.setAgitationText(Tr('unikalniy.drakon.so.skidkoy'));
                } else if (offer.offerType === OfferType.BRAND_DISCOUNT) {
                    this.tabbedPanel.setAgitationText(Tr('br.camp'));
                } else {
                    this.tabbedPanel.setAgitationText(Tr('uvelichte.svoy.otrjad.vplot.do..geroev'));
                }
            }
        }
    }

    public static showArtifactManual(scene: Scene, canvas: HTMLCanvasElement, listService: ListService, artifactId: string) {
        let conts: Container[] = [];

        let blackouter: ListBlackouter = listService.showSmart(scene!, canvas!, () => {});
        ArtifactStore.groupInfo(artifactId).then((gi: GroupInfo) => {
            blackouter.notifyLoaded(() => {
                if (gi.intro && gi.intro.length > 0) {
                    conts.push(new TextPanel(scene!, gi.intro));
                }
                conts.push(new TextPanel(scene!, '[size=18][color=#f0fdff]' + Tr('it.levs') + '[/color][/size]'));
                for (let li of gi.levels) {
                    let p = new ArtLevelLine(scene!, canvas!, li, li.level >= gi.levels.length);
                    conts.push(p);
                }
                if (gi.conclusion && gi.conclusion.length > 0) {
                    conts.push(new TextPanel(scene!, gi.conclusion));
                }

                blackouter.notifyInitialized(conts, gi.name);
            });
        });
    }
}

export enum TabName {
    BANK = "BANK",
    HERO = "HERO",
    ARTIFACT = "ARTIFACT",
    BOOSTER = "BOOSTER",
    CHAIN = "CHAIN",
    SALE = "SALE",
    GIFT = "GIFT",
}
